import { JSONSchema4 } from 'json-schema';

/**
 * Runtime contract for GET /people/{user}/basic-info
 * (EmployeeUpdateController::getBasicInfo).
 *
 * Deliberately permissive. `validateJsonResponseAsContract` in utils/Api throws an
 * AppError on a mismatch, which surfaces to the user as the whole screen erroring —
 * so this validates the *shape the form depends on* and nothing more:
 *
 *  - `required` covers only the fields the form cannot render without. Everything
 *    the form treats as optional stays optional here too.
 *  - Nullable fields are unions, matching what the controller emits for an
 *    employee whose record is only partly filled in.
 *  - `location` accepts a string as well as an integer — the FE only ever calls
 *    `.toString()` on it, and the PG/PHP boundary is not consistent about which
 *    it hands back.
 *  - No `additionalProperties: false`, so a field added to the controller later
 *    cannot break the page before the FE catches up.
 */
export const basicDetailsSchema: JSONSchema4 = {
  type: 'object',
  properties: {
    people_org_id: { type: 'integer' },
    name: { type: ['string', 'null'] },
    email: { type: ['string', 'null'] },
    is_email_editable: { type: 'boolean' },
    hire_date: { type: ['string', 'null'] },
    designation: { type: ['string', 'null'] },
    department: { type: ['string', 'null'] },
    manager_people_id: { type: ['integer', 'null'] },
    manager_name: { type: ['string', 'null'] },
    employee_type: { type: ['string', 'null'] },
    employee_sub_type: { type: ['string', 'null'] },
    is_employee_type_editable: { type: 'boolean' },
    employee_id: { type: ['string', 'null'] },
    is_employee_id_numeric: { type: 'boolean' },
    last_used_employee_id: { type: ['string', 'null'] },
    id_prefix: { type: ['string', 'null'] },
    employee_id_prefixes: { type: 'array', items: { type: 'string' } },
    contractor_id_prefixes: { type: 'array', items: { type: 'string' } },
    location: { type: ['integer', 'string', 'null'] },
    is_location_editable: { type: 'boolean' },
    office_location_name: { type: ['string', 'null'] },
    is_resident_of_india: { type: ['boolean', 'null'] },
    hrms_managed_fields: { type: 'array', items: { type: 'string' } },
    hrms_integration_name: { type: ['string', 'null'] },
  },
  required: ['is_email_editable', 'is_employee_type_editable'],
};

export default <JSONSchema4>{
  type: 'object',
  title: 'GetBasicInfoResponse',
  properties: {
    status: { type: 'string' },
    data: {
      type: 'object',
      properties: {
        basic_details: basicDetailsSchema,
      },
      required: ['basic_details'],
    },
  },
  required: ['data'],
};

/** PATCH /people/{user}/basic-info returns only `{ status: 'success' }`. */
export const updateBasicInfoResponseSchema: JSONSchema4 = {
  type: 'object',
  properties: {
    status: { type: 'string' },
  },
};

/** GET /validate-employee-id-availability/{userOrg}. */
export const employeeIdAvailabilityResponseSchema: JSONSchema4 = {
  type: 'object',
  properties: { isAvailable: { type: 'boolean' } },
  required: ['isAvailable'],
};

/** V1 api/generateEmailAddress.php. */
export const generatedEmailResponseSchema: JSONSchema4 = {
  type: 'object',
  properties: { email: { type: 'string' } },
  required: ['email'],
};
